import React, { useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Tabs, Tab } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useTranslation } from "react-i18next";
import { formatDateSk } from "app/utils/dateFormat";

const YearSummary = ({ data, currencyCode }) => {
  const isLoading = data == null;
  const { t, i18n } = useTranslation();
  const [chartType, setChartType] = useState("bar");
  const yesterdaySk = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return formatDateSk(d);
  }, []);

  const formatMonthTick = (tickValue) => {
    // tickValue is like "01", "02", ... created from monthLabel in data
    const monthIndex =
      typeof tickValue === "string"
        ? parseInt(tickValue, 10) - 1
        : Number(tickValue) - 1;
    if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return tickValue;
    }
    return new Date(2000, monthIndex, 1).toLocaleString(i18n.language || "en", {
      month: "short",
    });
  };

  const formatMonthLabel = (labelValue) => {
    const monthIndex =
      typeof labelValue === "string"
        ? parseInt(labelValue, 10) - 1
        : Number(labelValue) - 1;
    if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return labelValue;
    }
    return new Date(2000, monthIndex, 1).toLocaleString(i18n.language || "en", {
      month: "long",
    });
  };

  // Locale-aware number formatting helpers
  const locale = useMemo(() => {
    const lang = i18n.language || "en";
    if (lang === "sk") return "sk-SK";
    if (lang === "cs") return "cs-CZ";
    if (lang === "de") return "de-DE";
    return "en-US";
  }, [i18n.language]);

  const formatNumber = (value, fractionDigits = 2) => {
    const num = Number(value) || 0;
    return num.toLocaleString(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  const {
    chartData,
    years,
    yearTotals,
    comparisonRows,
    isSingleYear,
    singleYearRows,
    useThousands,
  } = useMemo(() => {
    const normalized = Array.isArray(data)
      ? data
      : data && typeof data === "object"
      ? Object.values(data)
      : [];
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const monthLabel = (m) => (m < 10 ? `0${m}` : String(m));

    const yearSet = new Set();
    const monthYearToTotal = new Map();

    (Array.isArray(normalized) ? normalized : []).forEach((item) => {
      const yRaw = item.year;
      const y =
        typeof yRaw === "object"
          ? Number(yRaw?.[i18n.language] ?? Object.values(yRaw)[0])
          : Number(yRaw);
      const m = item.month;
      const key = `${y}-${m}`;
      if (!Number.isNaN(y)) {
        yearSet.add(y);
      }
      const value = Number(item?.summary?.grandTotal || 0);
      monthYearToTotal.set(key, (monthYearToTotal.get(key) || 0) + value);
    });

    const sortedYears = Array.from(yearSet).sort((a, b) => a - b);
    const yearsToUse = sortedYears.slice(-2);
    const yearsDesc = yearsToUse.slice().sort((a, b) => b - a);

    const rows = months.map((m) => {
      const row = { month: monthLabel(m) };
      yearsDesc.forEach((y) => {
        const key = `${y}-${m}`;
        row[String(y)] = monthYearToTotal.get(key) ?? 0;
      });
      return row;
    });

    let maxMonthlyValue = 0;
    rows.forEach((r) => {
      yearsToUse.forEach((y) => {
        const val = Number(r[String(y)] || 0);
        if (val > maxMonthlyValue) {
          maxMonthlyValue = val;
        }
      });
    });

    const yearToMonthToValueLocal = {};
    yearsDesc.forEach((y) => {
      yearToMonthToValueLocal[y] = months.map((m) => ({
        month: m,
        value: rows[m - 1][String(y)] || 0,
      }));
    });

    const yearTotalsLocal = {};
    yearsDesc.forEach((y) => {
      const filtered = yearToMonthToValueLocal[y].filter(
        (r) => Number(r.value) !== 0
      );
      yearTotalsLocal[y] = filtered.reduce(
        (sum, r) => sum + Number(r.value || 0),
        0
      );
    });

    const isSingle = yearsDesc.length === 1;
    let comparison = [];
    let singleRows = [];
    if (isSingle) {
      const onlyYear = yearsDesc[0];
      singleRows = months
        .map((m) => ({
          month: m,
          label: monthLabel(m),
          value: rows[m - 1][String(onlyYear)] || 0,
        }))
        .filter((r) => Number(r.value) !== 0);
    } else {
      const newerYear = yearsDesc[0];
      const olderYear = yearsDesc[1];
      comparison = months
        .map((m) => {
          const olderVal =
            olderYear != null ? rows[m - 1][String(olderYear)] || 0 : 0;
          const newerVal =
            newerYear != null ? rows[m - 1][String(newerYear)] || 0 : 0;
          const diff = newerVal - olderVal;
          const pct = olderVal > 0 ? (diff / olderVal) * 100 : null;
          return {
            month: m,
            label: monthLabel(m),
            olderYear,
            newerYear,
            olderVal,
            newerVal,
            pct,
            direction:
              pct == null
                ? "flat"
                : diff > 0
                ? "up"
                : diff < 0
                ? "down"
                : "flat",
          };
        })
        .filter((r) => Number(r.olderVal) !== 0 || Number(r.newerVal) !== 0);
    }

    return {
      chartData: rows,
      years: yearsDesc,
      yearTotals: yearTotalsLocal,
      comparisonRows: comparison,
      isSingleYear: isSingle,
      singleYearRows: singleRows,
      useThousands: maxMonthlyValue > 10000,
    };
  }, [data, i18n.language]);

  return (
    <>
      {/* Chart */}
      <Grid item xs={12} md={8} lg={8}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.yearSummary.title")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : !data || data.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body2">{t("messages.noData")}</Typography>
            </Stack>
          ) : (
            <>
              <Box sx={{ mb: 1 }}>
                <Tabs
                  value={chartType}
                  onChange={(e, v) => setChartType(v)}
                  variant="fullWidth"
                >
                  <Tab
                    label={t("widgets.yearSummary.lineChart")}
                    value="line"
                  />
                  <Tab label={t("widgets.yearSummary.barChart")} value="bar" />
                </Tabs>
              </Box>
              {chartType === "line" ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickFormatter={formatMonthTick} />
                      <YAxis
                        tickFormatter={(v) => {
                          const formatted = useThousands
                            ? `${Number(v / 1000).toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })} ${t("widgets.yearSummary.thousands")}`
                            : Number(v).toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              });
                          return currencyCode
                            ? `${formatted} ${currencyCode}`
                            : formatted;
                        }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          formatNumber(value, 2) +
                            (currencyCode ? ` ${currencyCode}` : ""),
                          t("widgets.yearSummary.grandTotal"),
                        ]}
                        labelFormatter={formatMonthLabel}
                      />
                      <Legend />
                      {years.map((y, idx) => (
                        <Line
                          key={y}
                          type="monotone"
                          dataKey={String(y)}
                          stroke={
                            [
                              "#3f51b5",
                              "#e91e63",
                              "#009688",
                              "#ff9800",
                              "#9c27b0",
                            ][idx % 5]
                          }
                          dot={false}
                          strokeWidth={2}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickFormatter={formatMonthTick} />
                      <YAxis
                        tickFormatter={(v) => {
                          const formatted = useThousands
                            ? `${Number(v / 1000).toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })} ${t("widgets.yearSummary.thousands")}`
                            : Number(v).toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              });
                          return currencyCode
                            ? `${formatted} ${currencyCode}`
                            : formatted;
                        }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          formatNumber(value, 2) +
                            (currencyCode ? ` ${currencyCode}` : ""),
                          t("widgets.yearSummary.grandTotal"),
                        ]}
                        labelFormatter={formatMonthLabel}
                      />
                      <Legend />
                      {years.map((y, idx) => (
                        <Bar
                          key={y}
                          dataKey={String(y)}
                          fill={
                            [
                              "#3f51b5",
                              "#e91e63",
                              "#009688",
                              "#ff9800",
                              "#9c27b0",
                            ][idx % 5]
                          }
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("widgets.yearSummary.lastSync")} {yesterdaySk}
                </Typography>
              </Box>
            </>
          )}
        </JumboCardQuick>
      </Grid>

      {/* Comparison table for last 2 years */}
      <Grid item xs={12} md={4} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.yearSummary.monthlyTotals")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : !data || data.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Typography variant="body2">{t("messages.noData")}</Typography>
            </Stack>
          ) : isSingleYear ? (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="year-single">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("widgets.yearSummary.month")}</TableCell>
                    <TableCell align="right">{String(years[0])}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {singleYearRows.map((r) => (
                    <TableRow key={`single-${r.month}`}>
                      <TableCell>
                        {new Date(2000, r.month - 1, 1).toLocaleString(
                          i18n.language || "en",
                          { month: "short" }
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(r.value, 2)}
                        {currencyCode ? ` ${currencyCode}` : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{ "& .MuiTableCell-root": { fontWeight: "bold" } }}
                  >
                    <TableCell>{t("widgets.weekSummary.total")}</TableCell>
                    <TableCell align="right">
                      {formatNumber(yearTotals[years[0]] || 0, 2)}
                      {currencyCode ? ` ${currencyCode}` : ""}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="year-comparison">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("widgets.yearSummary.month")}</TableCell>
                    <TableCell align="right">{String(years[0])}</TableCell>
                    <TableCell align="right">{String(years[1])}</TableCell>
                    <TableCell align="right">
                      {t("widgets.yearSummary.trend")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonRows.map((r) => {
                    const label =
                      r.pct == null
                        ? "—"
                        : `${r.pct > 0 ? "+" : r.pct < 0 ? "-" : ""}${Math.abs(
                            r.pct
                          ).toFixed(2)}%`;
                    const color =
                      r.pct == null
                        ? "default"
                        : r.direction === "up"
                        ? "success.main"
                        : r.direction === "down"
                        ? "error.main"
                        : "text.secondary";
                    return (
                      <TableRow key={`cmp-${r.month}`}>
                        <TableCell>
                          {new Date(2000, r.month - 1, 1).toLocaleString(
                            i18n.language || "en",
                            { month: "short" }
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(r.newerVal, 2)}
                          {currencyCode ? ` ${currencyCode}` : ""}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(r.olderVal, 2)}
                          {currencyCode ? ` ${currencyCode}` : ""}
                        </TableCell>
                        <TableCell align="right" sx={{ color }}>
                          {label}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow
                    sx={{ "& .MuiTableCell-root": { fontWeight: "bold" } }}
                  >
                    <TableCell>{t("widgets.weekSummary.total")}</TableCell>
                    <TableCell align="right">
                      {formatNumber(yearTotals[years[0]] || 0, 2)}
                      {currencyCode ? ` ${currencyCode}` : ""}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(yearTotals[years[1]] || 0, 2)}
                      {currencyCode ? ` ${currencyCode}` : ""}
                    </TableCell>
                    {(() => {
                      const base = Number(yearTotals[years[1]] || 0); // older year
                      const curr = Number(yearTotals[years[0]] || 0); // newer year
                      const diff = curr - base;
                      const pct = base > 0 ? (diff / base) * 100 : null;
                      const label =
                        pct == null
                          ? "—"
                          : `${pct > 0 ? "+" : pct < 0 ? "-" : ""}${Math.abs(
                              pct
                            ).toFixed(2)}%`;
                      const color =
                        pct == null
                          ? "default"
                          : diff > 0
                          ? "success.main"
                          : diff < 0
                          ? "error.main"
                          : "text.secondary";
                      return (
                        <TableCell align="right" sx={{ color }}>
                          {label}
                        </TableCell>
                      );
                    })()}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </JumboCardQuick>
      </Grid>
    </>
  );
};

export default YearSummary;
